import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, RefreshCw, Save, Send, Square } from 'lucide-react';
import { toast } from 'sonner';
import {
  DiscordEmbedPreview,
  EmbedEditor,
  embedToForm,
  emptyForm,
  formToEmbed,
  type DiscordEmbed,
  type EmbedFormState,
} from '@raynex/discord-ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DiscordChannelPicker, DiscordRolePicker } from '@/components/discord/DiscordPickers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import {
  useGiveaway,
  useUpdateGiveaway,
  usePublishGiveawayNow,
  useScheduleGiveaway,
  useUnscheduleGiveaway,
  useEndGiveawayNow,
  useRerollGiveaway,
} from '@/lib/queries';
import { useCan } from '@/lib/use-can';

type MentionKind = 'none' | '@everyone' | '@here' | 'role';

function parseMention(value: string | null): { kind: MentionKind; roleId: string } {
  if (!value) return { kind: 'none', roleId: '' };
  if (value === '@everyone' || value === '@here') return { kind: value, roleId: '' };
  if (value.startsWith('role:')) return { kind: 'role', roleId: value.slice(5) };
  return { kind: 'none', roleId: '' };
}
function buildMention(kind: MentionKind, roleId: string): string | null {
  if (kind === 'none') return null;
  if (kind === '@everyone' || kind === '@here') return kind;
  return roleId ? `role:${roleId}` : null;
}
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  // Format the stored UTC instant as LOCAL wall-clock for the datetime-local
  // input, so it round-trips with `new Date(value)` on save (which parses a
  // tz-less datetime-local string as local). Stripping the `Z` instead showed
  // UTC digits as if local, drifting the time by the user's offset on re-save.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function GiveawayDetailPage() {
  const can = useCan();
  const { id = '' } = useParams();
  const detail = useGiveaway(id);
  const update = useUpdateGiveaway();
  const publishNow = usePublishGiveawayNow();
  const scheduleM = useScheduleGiveaway();
  const unschedule = useUnscheduleGiveaway();
  const endNow = useEndGiveawayNow();
  const reroll = useRerollGiveaway();
  const g = detail.data;

  const [name, setName] = useState('');
  const [prize, setPrize] = useState('');
  const [channelId, setChannelId] = useState('');
  const [winnerCount, setWinnerCount] = useState(1);
  const [requireRole, setRequireRole] = useState(false);
  const [requiredRoleId, setRequiredRoleId] = useState('');
  const [mentionKind, setMentionKind] = useState<MentionKind>('none');
  const [mentionRoleId, setMentionRoleId] = useState('');
  const [embedForm, setEmbedForm] = useState<EmbedFormState>(emptyForm());
  const [endsAt, setEndsAt] = useState('');
  const [startAt, setStartAt] = useState('');

  useEffect(() => {
    if (!g) return;
    setName(g.name);
    setPrize(g.prize);
    setChannelId(g.channelId);
    setWinnerCount(g.winnerCount);
    setRequireRole(!!g.requiredRoleId);
    setRequiredRoleId(g.requiredRoleId ?? '');
    const m = parseMention(g.mention);
    setMentionKind(m.kind);
    setMentionRoleId(m.roleId);
    setEmbedForm(embedToForm(g.embed as DiscordEmbed));
    setEndsAt(toLocalInput(g.endsAt));
    setStartAt(toLocalInput(g.startAt));
    // Only re-sync local form when the giveaway id changes, not on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g?.id]);

  const previewEmbed = useMemo(() => formToEmbed(embedForm), [embedForm]);

  if (!can('giveaways.manage')) {
    return <p className="text-muted-foreground">You don't have access to this page.</p>;
  }
  if (detail.isLoading) return <p className="text-muted-foreground">loading…</p>;
  if (!g) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/giveaways">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Link>
      </Button>
    );
  }

  async function saveCurrent(silent = false): Promise<boolean> {
    if (!channelId) {
      toast.error('Pick a channel');
      return false;
    }
    const when = new Date(endsAt);
    if (!Number.isFinite(when.getTime())) {
      toast.error('End time is invalid');
      return false;
    }
    try {
      await update.mutateAsync({
        id,
        patch: {
          name,
          prize,
          embed: formToEmbed(embedForm) as Record<string, unknown>,
          channelId,
          winnerCount,
          requiredRoleId: requireRole ? requiredRoleId || null : null,
          mention: buildMention(mentionKind, mentionRoleId),
          endsAt: when.toISOString(),
        },
      });
      if (!silent) toast.success('Saved');
      return true;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
      return false;
    }
  }

  async function handlePublishNow() {
    if (!confirm('Publish this giveaway now? Members can start entering immediately.')) return;
    if (!(await saveCurrent(true))) return;
    try {
      await publishNow.mutateAsync(id);
      toast.success('Publish queued');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleSchedule() {
    if (!startAt) {
      toast.error('Pick a start date/time first');
      return;
    }
    const when = new Date(startAt);
    if (!Number.isFinite(when.getTime()) || when.getTime() <= Date.now()) {
      toast.error('Start time must be in the future');
      return;
    }
    if (!(await saveCurrent(true))) return;
    try {
      await scheduleM.mutateAsync({ id, startAt: when.toISOString() });
      toast.success('Scheduled');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleEndNow() {
    if (!confirm('End this giveaway now and draw winners?')) return;
    try {
      await endNow.mutateAsync(id);
      toast.success('Draw queued');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleReroll() {
    if (!confirm('Reroll — draw new winners excluding the current ones?')) return;
    try {
      await reroll.mutateAsync({ id });
      toast.success('Reroll queued');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  const isActive = g.status === 'active';
  const isEnded = g.status === 'ended';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/giveaways">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to list
            </Link>
          </Button>
          <h1 className="mt-2 flex items-center gap-2 text-3xl font-bold tracking-tight">
            {name}
            <Badge variant={isActive ? 'default' : 'secondary'}>{g.status}</Badge>
            <span className="text-sm font-normal text-muted-foreground">
              {g.entryCount} entrant(s)
            </span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => saveCurrent(false)} disabled={update.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
          {!isEnded && (
            <Button onClick={handlePublishNow} disabled={publishNow.isPending || update.isPending}>
              <Send className="mr-2 h-4 w-4" />
              Publish now
            </Button>
          )}
          {isActive && (
            <Button variant="outline" onClick={handleEndNow} disabled={endNow.isPending}>
              <Square className="mr-2 h-4 w-4" />
              End now
            </Button>
          )}
          {isEnded && (
            <Button variant="outline" onClick={handleReroll} disabled={reroll.isPending}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reroll
            </Button>
          )}
        </div>
      </div>

      {isEnded && g.winnerUserIds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Winners</CardTitle>
            <CardDescription>{g.winnerUserIds.length} winner(s) drawn</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {g.winnerUserIds.map((w) => (
              <code key={w} className="mr-2">
                {w}
              </code>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name (internal label)</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prize">Prize</Label>
              <Input id="prize" value={prize} onChange={(e) => setPrize(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Channel</Label>
              <DiscordChannelPicker value={channelId} onValueChange={setChannelId} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="winnerCount">Number of winners</Label>
              <Input
                id="winnerCount"
                type="number"
                min={1}
                value={winnerCount}
                onChange={(e) => setWinnerCount(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="requireRole">Eligibility</Label>
              <Select
                value={requireRole ? 'role' : 'anyone'}
                onValueChange={(v) => setRequireRole(v === 'role')}
              >
                <SelectTrigger id="requireRole">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="anyone">Anyone can enter</SelectItem>
                  <SelectItem value="role">Require a role</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {requireRole && (
              <div className="space-y-2">
                <Label>Required role</Label>
                <DiscordRolePicker value={requiredRoleId} onValueChange={setRequiredRoleId} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mentionKind">Mention on post</Label>
              <Select value={mentionKind} onValueChange={(v) => setMentionKind(v as MentionKind)}>
                <SelectTrigger id="mentionKind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No mention</SelectItem>
                  <SelectItem value="@everyone">@everyone</SelectItem>
                  <SelectItem value="@here">@here</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mentionKind === 'role' && (
              <div className="space-y-2">
                <Label>Role to mention</Label>
                <DiscordRolePicker value={mentionRoleId} onValueChange={setMentionRoleId} />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="endsAt">Ends at (local time)</Label>
            <Input
              id="endsAt"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Embed</CardTitle>
          <CardDescription>
            The bot will automatically add the prize, time, winner count, and participant count.
            Placeholders {'{prize}'} {'{ends}'} {'{winnerCount}'} {'{count}'} {'{winners}'} are
            optional for inserting those values into your text.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EmbedEditor value={embedForm} onChange={setEmbedForm} />
            <div className="sticky top-4 self-start">
              <DiscordEmbedPreview embed={previewEmbed} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Schedule start</CardTitle>
          <CardDescription>
            {g.status === 'scheduled' && g.startAt
              ? `Starts ${new Date(g.startAt).toLocaleString()}`
              : 'Optionally schedule the giveaway to post automatically later'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="startAt">Start at (local time)</Label>
            <Input
              id="startAt"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              disabled={isActive || isEnded}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSchedule} disabled={scheduleM.isPending || isActive || isEnded}>
              <Calendar className="mr-2 h-4 w-4" />
              Schedule
            </Button>
            {g.status === 'scheduled' && (
              <Button
                variant="outline"
                onClick={() => unschedule.mutateAsync(id).then(() => toast.success('Unscheduled'))}
                disabled={unschedule.isPending}
              >
                Cancel schedule
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
